import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { OperationPayload, StepPayload, StructureReference } from './types';

type IdentifierNode = { type: 'identifier'; name: string };
type BinaryOpNode = {
    type: 'binary';
    op: BooleanOperation;
    left: BooleanOperationNode;
    right: BooleanOperationNode;
};
type UnaryOpNode = {
    type: 'unary';
    op: 'not';
    argument: BooleanOperationNode;
};
type FunctionNode = {
    type: 'fn';
    name: 'smooth' | 'erode';
    argument: BooleanOperationNode;
    valueMm: number;
};

export type BooleanOperationNode =
    | IdentifierNode
    | BinaryOpNode
    | UnaryOpNode
    | FunctionNode;

type Token =
    | { type: 'identifier'; value: string }
    | { type: 'lparen' }
    | { type: 'rparen' }
    | { type: 'operator'; value: string }
    | { type: 'number'; value: number }
    | { type: 'function'; value: 'SMOOTH' | 'ERODE' };

export function tokenize(input: string): Token[] {
    const TOKEN_REGEX =
        /"([^"]+)"|(\()|(\))|\b(SMOOTH|ERODE|AND_NOT|SUBTRACT|AND|OR|XOR|NOT)\b|(\d+\.?\d*)mm/gi;
    const tokens: Token[] = [];
    let match: RegExpExecArray | null;
    while ((match = TOKEN_REGEX.exec(input)) !== null) {
        if (match[1]) {
            tokens.push({ type: 'identifier', value: match[1] });
        } else if (match[2]) {
            tokens.push({ type: 'lparen' });
        } else if (match[3]) {
            tokens.push({ type: 'rparen' });
        } else if (match[4]) {
            const op = match[4].toUpperCase();
            if (op === 'SMOOTH' || op === 'ERODE') {
                tokens.push({ type: 'function', value: op });
            } else {
                tokens.push({ type: 'operator', value: op });
            }
        } else if (match[5]) {
            tokens.push({ type: 'number', value: parseFloat(match[5]) });
        }
    }
    return tokens;
}

const PRECEDENCE: Record<string, number> = {
    NOT: 3,
    AND: 2,
    AND_NOT: 2,
    SUBTRACT: 2,
    OR: 1,
    XOR: 1,
};

export function parseBooleanExpression(input: string): BooleanOperationNode {
    const tokens = tokenize(input);
    const outputStack: BooleanOperationNode[] = [];
    const opStack: Token[] = [];

    const applyOp = (op: Token) => {
        if (op.type === 'operator') {
            const upper = op.value.toUpperCase();
            if (upper === 'NOT') {
                const arg = outputStack.pop();
                if (!arg) {
                    throw new Error(
                        'Invalid expression: missing operand for NOT'
                    );
                }
                outputStack.push({ type: 'unary', op: 'not', argument: arg });
            } else {
                const right = outputStack.pop();
                const left = outputStack.pop();
                if (!left || !right) {
                    throw new Error(
                        `Invalid expression: missing operands for ${op.value}`
                    );
                }

                let booleanOp: BooleanOperation;
                switch (upper) {
                    case 'AND':
                        booleanOp = BooleanOperation.AND;
                        break;
                    case 'OR':
                        booleanOp = BooleanOperation.OR;
                        break;
                    case 'XOR':
                        booleanOp = BooleanOperation.XOR;
                        break;
                    case 'SUBTRACT':
                    case 'AND_NOT':
                        booleanOp = BooleanOperation.AND_NOT;
                        break;
                    default:
                        throw new Error(`Unknown boolean operator: ${upper}`);
                }

                outputStack.push({
                    type: 'binary',
                    op: booleanOp,
                    left,
                    right,
                });
            }
        } else if (op.type === 'function') {
            const arg = outputStack.pop();
            const valToken = opStack.pop();
            if (!arg) {
                throw new Error(
                    `Invalid expression: missing argument for ${op.value} (expected identifier)`
                );
            }
            if (!valToken || valToken.type !== 'number') {
                throw new Error(
                    `Invalid expression: missing number argument for ${op.value} (expected number token, got ${valToken ? valToken.type : 'null'})`
                );
            }
            outputStack.push({
                type: 'fn',
                name: op.value.toLowerCase() as 'smooth' | 'erode',
                argument: arg,
                valueMm: valToken.value,
            });
        }
    };

    for (let i = 0; i < tokens.length; i++) {
        const t = tokens[i];
        switch (t.type) {
            case 'identifier':
                outputStack.push({ type: 'identifier', name: t.value });
                break;
            case 'number':
                opStack.push(t);
                break;
            case 'function':
                opStack.push(t);
                break;
            case 'operator': {
                const currPrec = PRECEDENCE[t.value] ?? 0;
                while (opStack.length > 0) {
                    const top = opStack[opStack.length - 1];
                    if (
                        top?.type === 'operator' &&
                        (PRECEDENCE[top.value] ?? 0) >= currPrec
                    ) {
                        opStack.pop();
                        applyOp(top);
                    } else {
                        break;
                    }
                }
                opStack.push(t);
                break;
            }
            case 'lparen':
                opStack.push(t);
                break;
            case 'rparen':
                // Process operators until we find the matching lparen
                // Numbers and functions stay on the stack for function calls
                // We need to find the lparen, which may be below numbers/functions
                let lparenIndex = -1;
                for (let i = opStack.length - 1; i >= 0; i--) {
                    if (opStack[i]?.type === 'lparen') {
                        lparenIndex = i;
                        break;
                    }
                }

                if (lparenIndex === -1) {
                    throw new Error('Mismatched parentheses');
                }

                // Process operators between the lparen and the top of the stack
                while (opStack.length > lparenIndex + 1) {
                    const top = opStack[opStack.length - 1];
                    if (top?.type === 'operator') {
                        applyOp(opStack.pop() as Token);
                    } else {
                        // Numbers and functions stay on stack - stop processing
                        break;
                    }
                }

                // Remove the specific lparen at lparenIndex, preserving any tokens above it
                opStack.splice(lparenIndex, 1);
                // Check for function call: function may be below a number token
                // For SMOOTH("A" 2mm), after popping lparen, stack is [SMOOTH, 2mm]
                // For SMOOTH("A"), after popping lparen, stack is [SMOOTH]
                if (opStack.length > 0) {
                    const top = opStack[opStack.length - 1];
                    if (top?.type === 'function') {
                        // Function is directly on top: SMOOTH("A") with no number
                        // This case shouldn't happen with current syntax, but handle it
                        applyOp(opStack.pop() as Token);
                    } else if (top?.type === 'number' && opStack.length > 1) {
                        // Check if function is below number: SMOOTH("A" 2mm)
                        const functionToken = opStack[opStack.length - 2];
                        if (functionToken?.type === 'function') {
                            // Rearrange so number is on top when function pops it
                            const numberToken = opStack.pop() as Token;
                            const funcToken = opStack.pop() as Token;
                            opStack.push(numberToken);
                            applyOp(funcToken);
                        }
                    }
                }
                break;
        }
    }

    while (opStack.length) {
        const op = opStack.pop() as Token;
        if (op.type === 'lparen' || op.type === 'rparen') {
            throw new Error('Mismatched parentheses');
        }
        applyOp(op);
    }

    if (outputStack.length !== 1) {
        throw new Error('Invalid expression');
    }

    return outputStack[0];
}

/**
 * Gets the precedence of an operator for stringification.
 */
function getPrecedence(op: BooleanOperation): number {
    return PRECEDENCE[op.toUpperCase()] ?? 0;
}

/**
 * Checks if a node needs to be wrapped in parentheses when used as a child of a parent operation.
 * We wrap when:
 * - Child has lower or equal precedence (standard case)
 * - Child has higher precedence but is on the right side (for clarity and to preserve structure)
 */
function needsWrapping(
    child: BooleanOperationNode,
    parentOp: BooleanOperation,
    isRightOperand = false
): boolean {
    if (child.type === 'identifier') {
        return false;
    }
    if (child.type === 'binary') {
        const childPrec = getPrecedence(child.op);
        const parentPrec = getPrecedence(parentOp);
        // Wrap if child has lower or equal precedence
        // Also wrap right operands with higher precedence for clarity
        return (
            childPrec <= parentPrec ||
            (isRightOperand && childPrec > parentPrec)
        );
    }
    // Unary and function nodes always need wrapping when used as operands
    return true;
}

export function stringifyBooleanExpression(node: BooleanOperationNode): string {
    switch (node.type) {
        case 'identifier':
            return `"${node.name}"`;
        case 'unary':
            return `NOT ${stringifyBooleanExpression(node.argument)}`;
        case 'fn':
            return `${node.name.toUpperCase()}(${stringifyBooleanExpression(
                node.argument
            )}, ${node.valueMm}mm)`;
        case 'binary': {
            const opText =
                node.op === BooleanOperation.AND_NOT
                    ? 'SUBTRACT'
                    : node.op.toUpperCase();
            const leftStr = stringifyBooleanExpression(node.left);
            const rightStr = stringifyBooleanExpression(node.right);
            const left = needsWrapping(node.left, node.op, false)
                ? `(${leftStr})`
                : leftStr;
            const right = needsWrapping(node.right, node.op, true)
                ? `(${rightStr})`
                : rightStr;
            return `${left} ${opText} ${right}`;
        }
    }
}

export function buildFormulaFromSteps(steps: StepPayload[]): string {
    if (!steps.length) {
        return '';
    }

    let formula = '';
    steps.forEach((step, stepIndex) => {
        const leftStr =
            stepIndex === 0 && step.leftOperand !== 'previous-result'
                ? `"${step.leftOperand.roi.name}"`
                : 'PREVIOUS_RESULT';

        const rights = step.rightOperands
            .filter((s) => s && s.roi && s.roi.name)
            .map((s) => `"${s.roi.name}"`);

        if (rights.length === 0) {
            return;
        }

        const opText =
            step.operation === BooleanOperation.AND_NOT
                ? 'SUBTRACT'
                : step.operation.toUpperCase();

        const rightExpr =
            rights.length > 1 ? `(${rights.join(` ${opText} `)})` : rights[0];

        if (!rightExpr) {
            return;
        }

        if (!formula) {
            formula = `${leftStr} ${opText} ${rightExpr}`;
        } else {
            formula = `${formula} ${opText} ${rightExpr}`;
        }
    });

    return formula;
}

export function expressionTreeToStepPayloads(
    root: BooleanOperationNode,
    resolveStructure: (name: string) => StructureReference | null
): StepPayload[] {
    const steps: StepPayload[] = [];

    const processNodeForOperation = (
        node: BooleanOperationNode,
        targetOp: BooleanOperation
    ): string[] | null => {
        if (node.type === 'identifier') {
            return [node.name];
        }
        if (node.type === 'unary') {
            throw new Error('Unary NOT is not supported for execution yet.');
        }
        if (node.type === 'fn') {
            throw new Error(`${node.name} is not supported for execution yet.`);
        }

        if (node.type === 'binary') {
            if (node.op === targetOp) {
                const leftNames = processNodeForOperation(node.left, targetOp);
                const rightNames = processNodeForOperation(
                    node.right,
                    targetOp
                );
                if (leftNames === null || rightNames === null) {
                    return null;
                }
                return [...leftNames, ...rightNames];
            }
            return null;
        }

        throw new Error('Unsupported node type for execution');
    };

    const processBinaryNode = (node: BinaryOpNode): StepPayload[] => {
        const nodeSteps: StepPayload[] = [];

        const leftFlat = processNodeForOperation(node.left, node.op);
        let leftFirstStructure: StepPayload['leftOperand'];
        let leftAdditionalStructures: StructureReference[] = [];

        if (leftFlat === null) {
            if (node.left.type !== 'binary') {
                throw new Error(
                    'Expected binary operation for nested processing'
                );
            }
            const leftSteps = processBinaryNode(node.left);
            steps.push(...leftSteps);
            leftFirstStructure = 'previous-result';
        } else {
            if (leftFlat.length === 0) {
                throw new Error(
                    'Left side must resolve to at least one structure'
                );
            }
            const firstLeftName = leftFlat[0];
            if (!firstLeftName) {
                throw new Error('Left side structure name is undefined');
            }
            const leftStructure = resolveStructure(firstLeftName);
            if (!leftStructure) {
                throw new Error(`Structure "${firstLeftName}" not found`);
            }
            leftFirstStructure = leftStructure;

            leftAdditionalStructures = leftFlat.slice(1).map((name) => {
                const s = resolveStructure(name);
                if (!s) {
                    throw new Error(`Structure "${name}" not found`);
                }
                return s;
            });
        }

        const rightFlat = processNodeForOperation(node.right, node.op);
        let rightStructures: StructureReference[] = [];

        if (rightFlat === null) {
            if (node.right.type !== 'binary') {
                throw new Error(
                    'Expected binary operation for nested processing'
                );
            }
            const rightSteps = processBinaryNode(node.right);
            steps.push(...rightSteps);
            throw new Error(
                `Nested operations with different operators are not fully supported. ` +
                    `Expression "${stringifyBooleanExpression(
                        node
                    )}" contains nested operations that would require ` +
                    `using previous results as second operands, which is not yet implemented. ` +
                    `Please flatten the expression or use chained steps in the UI.`
            );
        } else {
            rightStructures = rightFlat.map((name) => {
                const s = resolveStructure(name);
                if (!s) {
                    throw new Error(`Structure "${name}" not found`);
                }
                return s;
            });
        }

        nodeSteps.push({
            leftOperand: leftFirstStructure,
            rightOperands: [...leftAdditionalStructures, ...rightStructures],
            operation: node.op,
        });

        return nodeSteps;
    };

    if (root.type === 'identifier') {
        throw new Error('Cannot execute a single identifier');
    }
    if (root.type === 'unary') {
        throw new Error('Unary NOT is not supported for execution yet.');
    }
    if (root.type === 'fn') {
        throw new Error(`${root.name} is not supported for execution yet.`);
    }

    const rootSteps = processBinaryNode(root);
    steps.push(...rootSteps);

    return steps;
}

export function buildPayloadFromText(
    text: string,
    resolveStructure: (name: string) => StructureReference | null,
    resultName: string
): OperationPayload {
    const ast = parseBooleanExpression(text);
    const steps = expressionTreeToStepPayloads(ast, resolveStructure);
    return { steps, resultName, formulaText: stringifyBooleanExpression(ast) };
}
